import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { businessPlanTemplates, categories, styleTags, BusinessPlanTemplate } from '../../mocks/businessPlanTemplates';
import ToolsDropdown from '../../components/feature/ToolsDropdown';
import LoginModal from '../home/components/LoginModal';
import { useFreeTrialGate } from '../../hooks/useFreeTrialGate';
import UpgradeTrialModal from '../../components/feature/UpgradeTrialModal';
import GenerateCustomPlanModal from './components/GenerateCustomPlanModal';
import TemplatePreviewModal from './components/TemplatePreviewModal';
import CompareModal from './components/CompareModal';

interface AttachedFile {
  id: string;
  file: File;
  preview?: string;
}

const BADGE_STYLES: Record<string, string> = {
  Popular: 'bg-amber-100 text-amber-700 border border-amber-200',
  New: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
  Trending: 'bg-rose-100 text-rose-700 border border-rose-200',
};

const ITEMS_PER_PAGE = 9;

const SORT_OPTIONS: { value: SortOption; label: string; icon: string }[] = [
  { value: 'popular', label: 'Most Popular', icon: 'ri-fire-line' },
  { value: 'newest', label: 'Newest', icon: 'ri-time-line' },
  { value: 'shortest', label: 'Shortest', icon: 'ri-sort-asc' },
  { value: 'longest', label: 'Longest', icon: 'ri-sort-desc' },
];

type SortOption = 'popular' | 'newest' | 'shortest' | 'longest';

const MAX_RECENTLY_VIEWED = 4;
const MAX_COMPARE = 3;
const FAVORITES_STORAGE_KEY = 'businessPlanFavorites';

// Industry Quick-Pick Cards Data
const POPULAR_INDUSTRIES = [
  {
    id: 'startup',
    name: 'Startup',
    category: 'Startup',
    icon: 'ri-rocket-line',
    image: 'https://public.readdy.ai/ai/img_res/6da3e35dcdf491437c30c67ea31ca2ae.jpg',
  },
  {
    id: 'ecommerce',
    name: 'E-commerce',
    category: 'E-commerce',
    icon: 'ri-shopping-cart-line',
    image: 'https://readdy.ai/api/search-image?query=modern%20ecommerce%20online%20shopping%20platform%20digital%20retail%20store%20with%20product%20displays%20clean%20white%20background%20professional%20commercial%20photography%20bright%20lighting%20minimalist%20aesthetic&width=800&height=500&seq=industry-ecommerce-1&orientation=landscape',
  },
  {
    id: 'restaurant',
    name: 'Restaurant & Food',
    category: 'Restaurant & Food',
    icon: 'ri-restaurant-line',
    image: 'https://readdy.ai/api/search-image?query=elegant%20modern%20restaurant%20interior%20with%20beautiful%20table%20settings%20ambient%20lighting%20professional%20culinary%20atmosphere%20clean%20minimalist%20design%20warm%20inviting%20environment%20fine%20dining%20aesthetic&width=800&height=500&seq=industry-restaurant-1&orientation=landscape',
  },
  {
    id: 'saas',
    name: 'SaaS & Tech',
    category: 'SaaS & Tech',
    icon: 'ri-cloud-line',
    image: 'https://readdy.ai/api/search-image?query=modern%20technology%20company%20office%20with%20software%20developers%20working%20on%20computers%20clean%20professional%20workspace%20bright%20natural%20light%20minimalist%20design%20innovative%20tech%20atmosphere&width=800&height=500&seq=industry-saas-1&orientation=landscape',
  },
  {
    id: 'healthcare',
    name: 'Healthcare',
    category: 'Healthcare',
    icon: 'ri-heart-pulse-line',
    image: 'https://readdy.ai/api/search-image?query=modern%20healthcare%20medical%20facility%20with%20clean%20professional%20environment%20bright%20white%20lighting%20medical%20equipment%20sterile%20atmosphere%20minimalist%20design%20professional%20healthcare%20setting&width=800&height=500&seq=industry-healthcare-1&orientation=landscape',
  },
  {
    id: 'realestate',
    name: 'Real Estate',
    category: 'Real Estate',
    icon: 'ri-building-line',
    image: 'https://readdy.ai/api/search-image?query=luxury%20modern%20real%20estate%20property%20showcase%20beautiful%20architecture%20professional%20photography%20bright%20natural%20lighting%20clean%20minimalist%20design%20elegant%20residential%20commercial%20buildings&width=800&height=500&seq=industry-realestate-1&orientation=landscape',
  },
  {
    id: 'hospitality',
    name: 'Hospitality',
    category: 'Hospitality',
    icon: 'ri-hotel-line',
    image: 'https://readdy.ai/api/search-image?query=luxury%20hotel%20lobby%20interior%20with%20elegant%20design%20professional%20hospitality%20atmosphere%20warm%20ambient%20lighting%20clean%20minimalist%20aesthetic%20welcoming%20environment%20five%20star%20service&width=800&height=500&seq=industry-hospitality-1&orientation=landscape',
  },
  {
    id: 'finance',
    name: 'Finance',
    category: 'Finance',
    icon: 'ri-line-chart-line',
    image: 'https://readdy.ai/api/search-image?query=modern%20financial%20services%20office%20with%20professional%20business%20atmosphere%20clean%20corporate%20design%20bright%20lighting%20minimalist%20aesthetic%20banking%20investment%20environment&width=800&height=500&seq=industry-finance-1&orientation=landscape',
  },
  {
    id: 'retail',
    name: 'Retail',
    category: 'Retail',
    icon: 'ri-store-line',
    image: 'https://readdy.ai/api/search-image?query=modern%20retail%20store%20interior%20with%20product%20displays%20clean%20commercial%20space%20bright%20professional%20lighting%20minimalist%20design%20shopping%20environment%20contemporary%20aesthetic&width=800&height=500&seq=industry-retail-1&orientation=landscape',
  },
  {
    id: 'consulting',
    name: 'Consulting',
    category: 'Consulting',
    icon: 'ri-user-star-line',
    image: 'https://readdy.ai/api/search-image?query=professional%20consulting%20office%20with%20business%20advisors%20meeting%20clients%20clean%20corporate%20environment%20bright%20natural%20lighting%20minimalist%20design%20professional%20atmosphere&width=800&height=500&seq=industry-consulting-1&orientation=landscape',
  },
];

function TemplateCard({
  template,
  onClick,
  compareIds,
  onToggleCompare,
  isFavorite,
  onToggleFavorite,
}: {
  template: BusinessPlanTemplate;
  onClick: () => void;
  compareIds: string[];
  onToggleCompare: (id: string) => void;
  isFavorite: boolean;
  onToggleFavorite: (id: string) => void;
}) {
  const [primary, accent] = template.colorPalette;
  const isSelected = compareIds.includes(template.id);
  const isDisabled = !isSelected && compareIds.length >= MAX_COMPARE;

  return (
    <div
      className={`group bg-white rounded-2xl border-2 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 overflow-hidden cursor-pointer flex flex-col ${
        isSelected
          ? 'border-violet-500 shadow-lg shadow-violet-100'
          : 'border-slate-200 hover:border-violet-300'
      }`}
    >
      {/* Image Preview */}
      <div
        className="relative h-48 overflow-hidden flex-shrink-0"
        onClick={onClick}
      >
        <img
          src={template.image}
          alt={template.name}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        {/* Gradient overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />

        <div className="absolute inset-0 flex items-end p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <span className="text-white text-sm font-semibold flex items-center gap-1.5 bg-black/30 backdrop-blur-sm px-3 py-1.5 rounded-lg">
            <i className="ri-eye-line"></i> Preview Template
          </span>
        </div>

        {template.badge && (
          <div className={`absolute top-3 left-3 px-2.5 py-0.5 rounded-full text-xs font-bold ${BADGE_STYLES[template.badge]}`}>
            {template.badge}
          </div>
        )}

        {/* Heart/Favorite button - top right */}
        <button
          onClick={e => { e.stopPropagation(); onToggleFavorite(template.id); }}
          className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-all cursor-pointer backdrop-blur-sm border ${
            isFavorite
              ? 'bg-rose-500 border-rose-500 text-white shadow-lg shadow-rose-200'
              : 'bg-white/90 border-white/50 text-slate-400 hover:text-rose-500'
          }`}
        >
          <i className={`${isFavorite ? 'ri-heart-fill' : 'ri-heart-line'} text-base`}></i>
        </button>

        <div className="absolute top-12 right-3 px-2.5 py-0.5 bg-white/90 backdrop-blur-sm rounded-full text-xs font-semibold text-slate-600 border border-slate-200">
          {template.pageCount}p
        </div>

        {/* Color palette strip */}
        <div className="absolute bottom-0 left-0 right-0 h-1 flex">
          {template.colorPalette.map((color, i) => (
            <div key={i} className="flex-1" style={{ background: color }} />
          ))}
        </div>

        {/* Compare checkbox overlay */}
        <button
          onClick={e => { e.stopPropagation(); if (!isDisabled) onToggleCompare(template.id); }}
          className={`absolute bottom-4 right-3 w-7 h-7 rounded-lg border-2 flex items-center justify-center transition-all cursor-pointer ${
            isSelected
              ? 'bg-violet-600 border-violet-600 text-white'
              : isDisabled
              ? 'bg-white/60 border-slate-300 text-slate-300 cursor-not-allowed'
              : 'bg-white/90 border-slate-300 text-slate-400 hover:border-violet-400 hover:text-violet-500'
          }`}
          title={isSelected ? 'Remove from compare' : isDisabled ? 'Max 3 templates' : 'Add to compare'}
        >
          {isSelected ? <i className="ri-check-line text-xs"></i> : <i className="ri-add-line text-xs"></i>}
        </button>
      </div>

      {/* Info */}
      <div className="p-5 flex flex-col flex-1" onClick={onClick}>
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-bold text-slate-900 text-sm leading-snug group-hover:text-violet-700 transition-colors line-clamp-2 flex-1">
            {template.name}
          </h3>
        </div>
        <p className="text-xs text-slate-500 line-clamp-2 mb-4 leading-relaxed">{template.description}</p>

        <div className="flex flex-wrap gap-1.5 mb-4">
          {template.tags.slice(0, 3).map(tag => (
            <span key={tag} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full font-medium">{tag}</span>
          ))}
        </div>

        <div className="mt-auto pt-4 border-t border-slate-100">
          <p className="text-xs text-slate-400 mb-2 font-medium uppercase tracking-wide">Includes</p>
          <div className="flex flex-wrap gap-1">
            {template.sections.slice(0, 4).map(s => (
              <span key={s} className="text-xs text-slate-500 flex items-center gap-1">
                <i className="ri-check-line text-emerald-500"></i>{s}
              </span>
            ))}
            {template.sections.length > 4 && (
              <span className="text-xs text-slate-400">+{template.sections.length - 4} more</span>
            )}
          </div>
        </div>
      </div>

      <div className="px-5 pb-5" onClick={onClick}>
        <button
          className="w-full py-2.5 rounded-xl text-sm font-semibold text-white transition-all whitespace-nowrap"
          style={{ background: `linear-gradient(135deg, ${primary}, ${accent})` }}
        >
          Use This Template
        </button>
      </div>
    </div>
  );
}

function RecentlyViewedRow({
  templates,
  onPreview,
  compareIds,
  onToggleCompare,
  favoriteIds,
  onToggleFavorite,
}: {
  templates: BusinessPlanTemplate[];
  onPreview: (t: BusinessPlanTemplate) => void;
  compareIds: string[];
  onToggleCompare: (id: string) => void;
  favoriteIds: string[];
  onToggleFavorite: (id: string) => void;
}) {
  if (templates.length === 0) return null;

  return (
    <div className="mb-10">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-6 h-6 flex items-center justify-center">
          <i className="ri-history-line text-violet-500 text-base"></i>
        </div>
        <h3 className="text-sm font-bold text-slate-800">Recently Viewed</h3>
        <span className="text-xs text-slate-400 ml-1">({templates.length})</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {templates.map(t => {
          const [primary] = t.colorPalette;
          const isSelected = compareIds.includes(t.id);
          const isDisabled = !isSelected && compareIds.length >= MAX_COMPARE;
          const isFavorite = favoriteIds.includes(t.id);
          return (
            <div
              key={t.id}
              className={`group bg-white rounded-xl border-2 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 overflow-hidden cursor-pointer ${
                isSelected ? 'border-violet-400 shadow-md shadow-violet-100' : 'border-slate-200 hover:border-violet-300'
              }`}
            >
              <div
                className="relative h-24 overflow-hidden"
                onClick={() => onPreview(t)}
              >
                <img
                  src={t.image}
                  alt={t.name}
                  className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <span className="text-white text-xs font-semibold bg-black/40 px-2 py-1 rounded-lg">Preview</span>
                </div>
                {/* Color strip */}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 flex">
                  {t.colorPalette.map((color, i) => (
                    <div key={i} className="flex-1" style={{ background: color }} />
                  ))}
                </div>
                {/* Heart button */}
                <button
                  onClick={e => { e.stopPropagation(); onToggleFavorite(t.id); }}
                  className={`absolute top-1.5 left-1.5 w-6 h-6 rounded-full flex items-center justify-center border transition-all cursor-pointer backdrop-blur-sm ${
                    isFavorite ? 'bg-rose-500 border-rose-500 text-white' : 'bg-white/90 border-white/50 text-slate-400'
                  }`}
                >
                  <i className={`${isFavorite ? 'ri-heart-fill' : 'ri-heart-line'} text-sm`}></i>
                </button>
                {/* Compare toggle */}
                <button
                  onClick={e => { e.stopPropagation(); if (!isDisabled) onToggleCompare(t.id); }}
                  className={`absolute top-1.5 right-1.5 w-5 h-5 rounded flex items-center justify-center border transition-all cursor-pointer ${
                    isSelected ? 'bg-violet-600 border-violet-600 text-white' : isDisabled ? 'bg-white/60 border-slate-300 text-slate-300 cursor-not-allowed' : 'bg-white/90 border-slate-300 text-slate-400 hover:border-violet-400'
                  }`}
                >
                  {isSelected ? <i className="ri-check-line" style={{ fontSize: 9 }}></i> : <i className="ri-add-line" style={{ fontSize: 9 }}></i>}
                </button>
              </div>
              <div className="p-3" onClick={() => onPreview(t)}>
                <p className="text-xs font-bold text-slate-800 line-clamp-1 group-hover:text-violet-700 transition-colors">{t.name}</p>
                <p className="text-xs text-slate-400 mt-0.5">{t.pageCount}p · {t.category}</p>
                <div className="flex items-center gap-1 mt-1.5">
                  {t.colorPalette.map((color, i) => (
                    <div key={i} className="w-3 h-3 rounded-full border border-white shadow-sm" style={{ background: color }} />
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Autocomplete helpers ───────────────────────────────────────────────────

function highlight(text: string, query: string): JSX.Element {
  if (!query) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-violet-100 text-violet-800 font-bold rounded px-0.5">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
}

interface Suggestion {
  type: 'template' | 'category' | 'tag';
  label: string;
  sub?: string;
  template?: BusinessPlanTemplate;
}

const MAX_SUGGESTIONS = 8;

export default function BusinessPlanWriter() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [showAttachments, setShowAttachments] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showGeneratePlan, setShowGeneratePlan] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<BusinessPlanTemplate | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('popular');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [compareIds, setCompareIds] = useState<string[]>([]);
  const [showCompare, setShowCompare] = useState(false);
  const [recentlyViewed, setRecentlyViewed] = useState<BusinessPlanTemplate[]>([]);
  const [showTagPanel, setShowTagPanel] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState<string[]>([]);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  // Autocomplete state
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Scroll to templates section ref
  const templatesSectionRef = useRef<HTMLDivElement>(null);

  // Build suggestions from query
  const suggestions = useMemo<Suggestion[]>(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q || q.length < 1) return [];

    const results: Suggestion[] = [];
    const seen = new Set<string>();

    // Matching categories
    categories.slice(1).forEach(cat => {
      if (cat.toLowerCase().includes(q) && !seen.has('cat:' + cat)) {
        seen.add('cat:' + cat);
        results.push({ type: 'category', label: cat, sub: 'Category' });
      }
    });

    // Matching style tags
    styleTags.forEach(tag => {
      if (tag.toLowerCase().includes(q) && !seen.has('tag:' + tag)) {
        seen.add('tag:' + tag);
        results.push({ type: 'tag', label: tag, sub: 'Style Tag' });
      }
    });

    // Matching templates (by name first, then description)
    const nameMatches: BusinessPlanTemplate[] = [];
    const descMatches: BusinessPlanTemplate[] = [];

    businessPlanTemplates.forEach(t => {
      if (t.name.toLowerCase().includes(q)) nameMatches.push(t);
      else if (t.description.toLowerCase().includes(q)) descMatches.push(t);
    });

    const combined = [...nameMatches, ...descMatches];
    const remaining = MAX_SUGGESTIONS - results.length;
    combined.slice(0, remaining).forEach(t => {
      if (!seen.has('tpl:' + t.id)) {
        seen.add('tpl:' + t.id);
        results.push({ type: 'template', label: t.name, sub: t.category, template: t });
      }
    });

    return results.slice(0, MAX_SUGGESTIONS);
  }, [searchQuery]);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
        setActiveSuggestionIndex(-1);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearchChange = (val: string) => {
    setSearchQuery(val);
    setCurrentPage(1);
    setShowSuggestions(val.trim().length > 0);
    setActiveSuggestionIndex(-1);
  };

  const applySuggestion = (s: Suggestion) => {
    if (s.type === 'template' && s.template) {
      setSearchQuery('');
      setShowSuggestions(false);
      handlePreviewTemplate(s.template);
    } else if (s.type === 'category') {
      setSearchQuery('');
      setShowSuggestions(false);
      handleCategoryChange(s.label);
      document.getElementById('templates-section')?.scrollIntoView({ behavior: 'smooth' });
    } else if (s.type === 'tag') {
      setSearchQuery('');
      setShowSuggestions(false);
      if (!selectedTags.includes(s.label)) {
        setSelectedTags(prev => [...prev, s.label]);
        setCurrentPage(1);
      }
      document.getElementById('templates-section')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestionIndex(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestionIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (activeSuggestionIndex >= 0) {
        e.preventDefault();
        applySuggestion(suggestions[activeSuggestionIndex]);
      } else {
        setShowSuggestions(false);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setActiveSuggestionIndex(-1);
    }
  };

  const handlePreviewTemplate = useCallback((template: BusinessPlanTemplate) => {
    setPreviewTemplate(template);
    setRecentlyViewed(prev => {
      const filtered = prev.filter(t => t.id !== template.id);
      return [template, ...filtered].slice(0, MAX_RECENTLY_VIEWED);
    });
  }, []);

  const handleToggleCompare = useCallback((id: string) => {
    setCompareIds(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= MAX_COMPARE) return prev;
      return [...prev, id];
    });
  }, []);

  const handleToggleTag = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
    setCurrentPage(1);
  };

  const handleToggleFavorite = useCallback((id: string) => {
    setFavoriteIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(x => x !== id);
      }
      return [...prev, id];
    });
  }, []);

  const compareTemplates = useMemo(
    () => businessPlanTemplates.filter(t => compareIds.includes(t.id)),
    [compareIds]
  );

  const filteredTemplates = useMemo(() => {
    let filtered = businessPlanTemplates.filter(t => {
      const q = searchQuery.toLowerCase();
      const matchesSearch = !q || t.name.toLowerCase().includes(q) || t.description.toLowerCase().includes(q) || t.tags.some(tag => tag.toLowerCase().includes(q));
      const matchesCategory = selectedCategory === 'All' || t.category === selectedCategory;
      const matchesTags = selectedTags.length === 0 || selectedTags.every(st => t.tags.includes(st));
      const matchesFavorites = !showFavoritesOnly || favoriteIds.includes(t.id);
      return matchesSearch && matchesCategory && matchesTags && matchesFavorites;
    });

    const sorted = [...filtered];
    if (sortBy === 'shortest') sorted.sort((a, b) => a.pageCount - b.pageCount);
    else if (sortBy === 'longest') sorted.sort((a, b) => b.pageCount - a.pageCount);
    else if (sortBy === 'newest') sorted.sort((a, b) => b.id.localeCompare(a.id));
    else {
      sorted.sort((a, b) => {
        const badgeScore = (t: BusinessPlanTemplate) => t.badge === 'Popular' ? 3 : t.badge === 'Trending' ? 2 : t.badge === 'New' ? 1 : 0;
        return badgeScore(b) - badgeScore(a);
      });
    }
    return sorted;
  }, [searchQuery, selectedCategory, selectedTags, sortBy, showFavoritesOnly, favoriteIds]);

  const totalPages = Math.ceil(filteredTemplates.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentTemplates = filteredTemplates.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleCategoryChange = (cat: string) => { 
    setSelectedCategory(cat); 
    setCurrentPage(1); 
    // Smooth scroll to templates section
    setTimeout(() => {
      templatesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleSortChange = (val: SortOption) => { setSortBy(val); setCurrentPage(1); setShowSortDropdown(false); };

  const addFiles = (files: File[]) => {
    const validFiles = files.filter(f => f.size <= 25 * 1024 * 1024);
    if (attachedFiles.length + validFiles.length > 10) return;
    const newFiles: AttachedFile[] = validFiles.map(file => {
      const attached: AttachedFile = { id: Math.random().toString(36).substr(2, 9), file };
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setAttachedFiles(prev => prev.map(f => f.id === attached.id ? { ...f, preview: e.target?.result as string } : f));
        };
        reader.readAsDataURL(file);
      }
      return attached;
    });
    setAttachedFiles(prev => [...prev, ...newFiles]);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => addFiles(Array.from(e.target.files || []));
  const handleDrop = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); addFiles(Array.from(e.dataTransfer.files)); };
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const removeFile = (id: string) => setAttachedFiles(prev => prev.filter(f => f.id !== id));
  const formatFileSize = (bytes: number) => bytes < 1024 ? bytes + ' B' : bytes < 1024 * 1024 ? (bytes / 1024).toFixed(1) + ' KB' : (bytes / (1024 * 1024)).toFixed(1) + ' MB';

  const getPaginationPages = () => {
    const pages: (number | '...')[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const activeSortLabel = SORT_OPTIONS.find(o => o.value === sortBy)?.label || 'Most Popular';
  const activeFilterCount = selectedTags.length + (selectedCategory !== 'All' ? 1 : 0) + (showFavoritesOnly ? 1 : 0);

  // Auto-open template from shared link
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const templateId = params.get('template');
    if (templateId) {
      const found = businessPlanTemplates.find(t => t.id === templateId);
      if (found) {
        handlePreviewTemplate(found);
      }
    }
  }, [location.search, handlePreviewTemplate]);

  // Load favorites from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setFavoriteIds(parsed);
        }
      }
    } catch (e) {
      console.error('Failed to load favorites:', e);
    }
  }, []);

  // Save favorites to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favoriteIds));
    } catch (e) {
      console.error('Failed to save favorites:', e);
    }
  }, [favoriteIds]);

  // Get template count for each industry
  const getIndustryTemplateCount = (category: string) => {
    return businessPlanTemplates.filter(t => t.category === category).length;
  };

  const { exhausted: trialExhausted, consume: consumeTrial, remaining: trialRemaining, dots: trialDots, used: trialUsed } = useFreeTrialGate('business-plan-writer');
  const [showUpgradeTrial, setShowUpgradeTrial] = useState(false);

  /** Use instead of setShowLogin(true) for "use template" / "generate" actions */
  const handleUseAction = useCallback(() => {
    if (trialExhausted) {
      setShowUpgradeTrial(true);
      return;
    }
    consumeTrial();
    setShowLogin(true);
  }, [trialExhausted, consumeTrial]);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => window.REACT_APP_NAVIGATE('/')}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer"
            >
              <div className="w-9 h-9 bg-gradient-to-br from-violet-600 to-fuchsia-600 rounded-xl flex items-center justify-center">
                <i className="ri-file-text-line text-white text-lg"></i>
              </div>
              <div>
                <div className="text-lg font-bold text-slate-900">openclaw.ai</div>
                <div className="text-xs text-slate-400 -mt-0.5">Business Plan Writer</div>
              </div>
            </button>
            <div className="hidden md:flex items-center gap-1.5 px-3 py-1.5 bg-violet-50 border border-violet-200 rounded-full">
              <div className="w-1.5 h-1.5 bg-violet-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-semibold text-violet-700">OpenClaw Algorithm</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ToolsDropdown isDarkMode={false} />
            {/* Trial counter */}
            {!trialExhausted ? (
              <div className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 bg-violet-50 border border-violet-200 rounded-full">
                <div className="flex gap-1">
                  {trialDots.map((d, i) => (
                    <div key={i} className={`w-1.5 h-1.5 rounded-full ${d.active ? 'bg-gray-300' : 'bg-violet-500'}`} />
                  ))}
                </div>
                <span className="text-[10px] font-semibold text-violet-700">{trialRemaining} free use{trialRemaining !== 1 ? 's' : ''}</span>
              </div>
            ) : (
              <button onClick={() => setShowUpgradeTrial(true)} className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-xs font-bold rounded-full cursor-pointer hover:opacity-90 whitespace-nowrap">
                <i className="ri-sparkling-2-fill text-xs"></i>Upgrade
              </button>
            )}
            <button
              onClick={() => setShowLogin(true)}
              className="text-sm font-semibold px-4 py-2 rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50 transition-all whitespace-nowrap cursor-pointer"
            >
              Sign In
            </button>
            <button
              onClick={handleUseAction}
              className="text-sm font-semibold px-4 py-2 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:opacity-90 transition-all whitespace-nowrap cursor-pointer"
            >
              Get Started Free
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-br from-violet-700 via-violet-600 to-fuchsia-600 text-white">
        <div className="max-w-5xl mx-auto px-6 py-16 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/15 backdrop-blur-sm rounded-full border border-white/25 mb-6">
            <i className="ri-sparkling-line text-amber-300 text-sm"></i>
            <span className="text-lg font-semibold">500,000+ Business Plans Generated</span>
          </div>
          <h1 className="text-5xl font-extrabold mb-4 tracking-tight">AI Business Plan Writer</h1>
          <p className="text-lg text-white/80 max-w-xl mx-auto mb-8">
            Create investor-ready business plans in minutes. Choose a template, customize with AI, and impress stakeholders.
          </p>
          <div className="flex items-center justify-center gap-3 mb-8 flex-wrap">
            <button
              onClick={() => setShowGeneratePlan(true)}
              className="flex items-center gap-2 px-6 py-3 bg-white text-violet-700 font-bold rounded-xl hover:bg-slate-50 transition-colors text-sm whitespace-nowrap cursor-pointer"
            >
              <i className="ri-magic-line text-base"></i>
              Generate Custom Plan
            </button>
            <button
              onClick={() => document.getElementById('templates-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="flex items-center gap-2 px-6 py-3 bg-white/15 border border-white/30 text-white font-semibold rounded-xl hover:bg-white/25 transition-colors text-sm whitespace-nowrap cursor-pointer"
            >
              <i className="ri-layout-grid-line text-base"></i>
              Browse Templates
            </button>
          </div>

          {/* Search with Autocomplete */}
          <div className="max-w-xl mx-auto relative" ref={searchRef}>
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-lg z-10"></i>
            <input
              ref={inputRef}
              type="text"
              placeholder="Search templates by industry, type, or keyword..."
              value={searchQuery}
              onChange={e => handleSearchChange(e.target.value)}
              onFocus={() => { if (searchQuery.trim().length > 0) setShowSuggestions(true); }}
              onKeyDown={handleKeyDown}
              className="w-full pl-11 pr-28 py-3.5 bg-white rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-white/50 text-sm shadow-xl"
            />
            <button
              onClick={() => setShowAttachments(!showAttachments)}
              className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-attachment-line text-sm"></i>
              <span className="text-xs font-semibold">Attach</span>
              {attachedFiles.length > 0 && (
                <span className="w-4 h-4 bg-violet-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {attachedFiles.length}
                </span>
              )}
            </button>

            {/* Autocomplete Dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-2 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden z-50 text-left">
                <div className="px-4 pt-3 pb-1.5 flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Suggestions</span>
                  <button
                    onMouseDown={e => { e.preventDefault(); setShowSuggestions(false); }}
                    className="text-slate-300 hover:text-slate-500 cursor-pointer"
                  >
                    <i className="ri-close-line text-sm"></i>
                  </button>
                </div>
                <ul>
                  {suggestions.map((s, idx) => {
                    const isActive = idx === activeSuggestionIndex;
                    const iconMap: Record<string, string> = {
                      template: 'ri-file-text-line',
                      category: 'ri-grid-line',
                      tag: 'ri-price-tag-3-line',
                    };
                    const colorMap: Record<string, string> = {
                      template: 'text-violet-500 bg-violet-50',
                      category: 'text-emerald-500 bg-emerald-50',
                      tag: 'text-amber-500 bg-amber-50',
                    };
                    return (
                      <li key={idx}>
                        <button
                          onMouseDown={e => { e.preventDefault(); applySuggestion(s); }}
                          onMouseEnter={() => setActiveSuggestionIndex(idx)}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 transition-colors cursor-pointer text-left ${isActive ? 'bg-violet-50' : 'hover:bg-slate-50'}`}
                        >
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${colorMap[s.type]}`}>
                            <i className={`${iconMap[s.type]} text-sm`}></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-slate-800 truncate">
                              {highlight(s.label, searchQuery.trim())}
                            </p>
                            {s.sub && <p className="text-xs text-slate-400 mt-0.5">{s.sub}</p>}
                          </div>
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                            <i className={`ri-arrow-right-up-line text-xs ${isActive ? 'text-violet-400' : 'text-slate-300'}`}></i>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
                <div className="px-4 py-2.5 border-t border-slate-100 flex items-center gap-2 bg-slate-50/60">
                  <span className="text-xs text-slate-400">Press</span>
                  <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded text-xs text-slate-500 font-mono">↑↓</kbd>
                  <span className="text-xs text-slate-400">to navigate</span>
                  <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded text-xs text-slate-500 font-mono">↵</kbd>
                  <span className="text-xs text-slate-400">to select</span>
                  <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded text-xs text-slate-500 font-mono ml-auto">Esc</kbd>
                  <span className="text-xs text-slate-400">to close</span>
                </div>
              </div>
            )}
          </div>

          {/* Attachment Panel */}
          {showAttachments && (
            <div className="max-w-xl mx-auto mt-3 bg-white rounded-xl p-5 shadow-2xl text-left">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-slate-900">Attach Reference Files</h3>
                <button onClick={() => setShowAttachments(false)} className="text-slate-400 hover:text-slate-600 cursor-pointer">
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={`border-2 border-dashed rounded-xl p-6 text-center transition-all ${isDragging ? 'border-violet-500 bg-violet-50' : 'border-slate-200 bg-slate-50 hover:border-slate-300'}`}
              >
                <i className={`ri-upload-cloud-2-line text-3xl mb-2 ${isDragging ? 'text-violet-500' : 'text-slate-400'}`}></i>
                <p className="text-xs text-slate-500 mb-3">Drag &amp; drop or click to browse · Max 25MB · Up to 10 files</p>
                <input type="file" multiple accept="image/*,video/*,.pdf,.doc,.docx,.txt,.csv" onChange={handleFileSelect} className="hidden" id="file-upload" />
                <label htmlFor="file-upload" className="inline-block px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold rounded-lg cursor-pointer transition-colors whitespace-nowrap">
                  Choose Files
                </label>
              </div>
              {attachedFiles.length > 0 && (
                <div className="mt-4 space-y-2">
                  {attachedFiles.map(file => (
                    <div key={file.id} className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-lg group">
                      {file.preview
                        ? <img src={file.preview} alt={file.file.name} className="w-10 h-10 object-cover rounded-lg flex-shrink-0" />
                        : <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <i className="ri-file-text-line text-violet-600 text-sm"></i>
                          </div>
                      }
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-slate-800 truncate">{file.file.name}</p>
                        <p className="text-xs text-slate-400">{formatFileSize(file.file.size)}</p>
                      </div>
                      <button onClick={() => removeFile(file.id)} className="text-slate-300 hover:text-red-500 transition-colors cursor-pointer opacity-0 group-hover:opacity-100">
                        <i className="ri-delete-bin-6-line text-sm"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <span className="flex items-center gap-1.5"><i className="ri-file-list-3-line text-violet-500"></i><strong className="text-slate-800">{filteredTemplates.length.toLocaleString()}</strong> templates</span>
            <span className="flex items-center gap-1.5"><i className="ri-grid-line text-violet-500"></i><strong className="text-slate-800">{categories.length - 1}</strong> industries</span>
            <span className="flex items-center gap-1.5"><i className="ri-heart-fill text-rose-500"></i><strong className="text-slate-800">{favoriteIds.length}</strong> saved</span>
            <span className="flex items-center gap-1.5"><i className="ri-ai-generate text-violet-500"></i>AI-powered generation</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <i className="ri-shield-check-line text-emerald-500"></i>
            Investor-ready · Bank-approved formats
          </div>
        </div>
      </div>

      {/* Industry Quick-Pick Section */}
      <div className="bg-gradient-to-b from-white to-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-extrabold text-slate-900 mb-2">Browse by Industry</h2>
            <p className="text-slate-500 text-sm">Select your industry to explore tailored business plan templates</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {POPULAR_INDUSTRIES.map(industry => {
              const templateCount = getIndustryTemplateCount(industry.category);
              const isActive = selectedCategory === industry.category;
              
              return (
                <button
                  key={industry.id}
                  onClick={() => handleCategoryChange(industry.category)}
                  className={`group relative overflow-hidden rounded-2xl transition-all duration-300 cursor-pointer ${
                    isActive
                      ? 'ring-4 ring-violet-500 shadow-2xl shadow-violet-200 scale-105'
                      : 'hover:shadow-xl hover:scale-105 hover:ring-2 hover:ring-violet-300'
                  }`}
                >
                  {/* Background Image */}
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={industry.image}
                      alt={industry.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    {/* Gradient Overlay */}
                    <div className={`absolute inset-0 transition-all duration-300 ${
                      isActive
                        ? 'bg-gradient-to-t from-violet-900/90 via-violet-700/60 to-violet-500/30'
                        : 'bg-gradient-to-t from-slate-900/80 via-slate-800/50 to-slate-700/20 group-hover:from-violet-900/80 group-hover:via-violet-800/50 group-hover:to-violet-700/20'
                    }`}></div>
                    
                    {/* Active Indicator */}
                    {isActive && (
                      <div className="absolute top-3 right-3 w-7 h-7 bg-white rounded-full flex items-center justify-center shadow-lg">
                        <i className="ri-check-line text-violet-600 text-sm font-bold"></i>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
                    {/* Icon */}
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-3 transition-all duration-300 ${
                      isActive
                        ? 'bg-white shadow-xl scale-110'
                        : 'bg-white/90 backdrop-blur-sm group-hover:bg-white group-hover:scale-110 group-hover:shadow-xl'
                    }`}>
                      <i className={`${industry.icon} text-2xl ${
                        isActive ? 'text-violet-600' : 'text-slate-700 group-hover:text-violet-600'
                      }`}></i>
                    </div>

                    {/* Industry Name */}
                    <h3 className={`font-bold text-base mb-1.5 transition-all duration-300 ${
                      isActive
                        ? 'text-white'
                        : 'text-white group-hover:text-white'
                    }`}>
                      {industry.name}
                    </h3>

                    {/* Template Count Badge */}
                    <div className={`px-3 py-1 rounded-full text-xs font-bold transition-all duration-300 ${
                      isActive
                        ? 'bg-white text-violet-700 shadow-lg'
                        : 'bg-white/90 text-slate-700 group-hover:bg-white group-hover:text-violet-700 group-hover:shadow-lg'
                    }`}>
                      {templateCount} template{templateCount !== 1 ? 's' : ''}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* View All Button */}
          <div className="text-center mt-8">
            <button
              onClick={() => handleCategoryChange('All')}
              className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all cursor-pointer whitespace-nowrap ${
                selectedCategory === 'All'
                  ? 'bg-violet-600 text-white shadow-lg shadow-violet-200'
                  : 'bg-white border-2 border-slate-200 text-slate-700 hover:border-violet-300 hover:text-violet-700 hover:shadow-md'
              }`}
            >
              <i className="ri-apps-line text-base"></i>
              View All Industries
              <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs font-bold">
                {businessPlanTemplates.length}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Main */}
      <div id="templates-section" ref={templatesSectionRef} className="max-w-7xl mx-auto px-6 py-10">

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-5">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => handleCategoryChange(cat)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                selectedCategory === cat
                  ? 'bg-violet-600 text-white shadow-md shadow-violet-200'
                  : 'bg-white border-2 border-slate-200 text-slate-600 hover:border-violet-300 hover:text-violet-700'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Tag Filter Panel Toggle & Favorites Toggle */}
        <div className="mb-6 flex items-center gap-3 flex-wrap">
          <button
            onClick={() => setShowTagPanel(v => !v)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
              showTagPanel || selectedTags.length > 0
                ? 'bg-violet-50 border-violet-300 text-violet-700'
                : 'bg-white border-slate-200 text-slate-600 hover:border-violet-300 hover:text-violet-700'
            }`}
          >
            <i className="ri-price-tag-3-line text-base"></i>
            Style Tags
            {selectedTags.length > 0 && (
              <span className="w-5 h-5 bg-violet-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                {selectedTags.length}
              </span>
            )}
            <i className={`ri-arrow-down-s-line text-base transition-transform ${showTagPanel ? 'rotate-180' : ''}`}></i>
          </button>

          {/* Favorites Filter Button */}
          <button
            onClick={() => { setShowFavoritesOnly(v => !v); setCurrentPage(1); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
              showFavoritesOnly
                ? 'bg-rose-50 border-rose-300 text-rose-700'
                : 'bg-white border-slate-200 text-slate-600 hover:border-rose-300 hover:text-rose-700'
            }`}
          >
            <i className={`${showFavoritesOnly ? 'ri-heart-fill' : 'ri-heart-line'} text-base`}></i>
            Saved Templates
            {favoriteIds.length > 0 && (
              <span className={`w-5 h-5 text-xs font-bold rounded-full flex items-center justify-center ${
                showFavoritesOnly ? 'bg-rose-600 text-white' : 'bg-slate-200 text-slate-600'
              }`}>
                {favoriteIds.length}
              </span>
            )}
          </button>
        </div>

        {showTagPanel && (
          <div className="mt-3 mb-6 p-5 bg-white rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm font-bold text-slate-800">Filter by Style</p>
                <p className="text-xs text-slate-400 mt-0.5">Select one or more style tags to narrow results</p>
              </div>
              {selectedTags.length > 0 && (
                <button
                  onClick={() => { setSelectedTags([]); setCurrentPage(1); }}
                  className="text-xs text-violet-600 hover:text-violet-800 font-semibold cursor-pointer whitespace-nowrap flex items-center gap-1"
                >
                  <i className="ri-close-circle-line"></i> Clear tags
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {styleTags.map(tag => {
                const isActive = selectedTags.includes(tag);
                const tagIcons: Record<string, string> = {
                  Minimal: 'ri-subtract-line',
                  Professional: 'ri-briefcase-line',
                  Detailed: 'ri-file-list-3-line',
                  'Investor-Ready': 'ri-funds-line',
                  Concise: 'ri-compress-line',
                  'Data-Driven': 'ri-bar-chart-2-line',
                  Visual: 'ri-image-line',
                  Academic: 'ri-graduation-cap-line',
                  Modern: 'ri-sparkling-line',
                  Traditional: 'ri-building-line',
                  Bold: 'ri-contrast-2-line',
                  Clean: 'ri-leaf-line',
                };
                return (
                  <button
                    key={tag}
                    onClick={() => handleToggleTag(tag)}
                    className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                      isActive
                        ? 'bg-violet-600 text-white border-violet-600 shadow-md shadow-violet-200'
                        : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-violet-300 hover:text-violet-700 hover:bg-violet-50'
                    }`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${tagIcons[tag] || 'ri-price-tag-3-line'} text-sm`}></i>
                    </div>
                    {tag}
                    {isActive && <i className="ri-check-line text-xs ml-0.5"></i>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Active filter chips */}
        {activeFilterCount > 0 && (
          <div className="flex items-center gap-2 mb-5 flex-wrap">
            <span className="text-xs text-slate-400 font-medium">Active filters:</span>
            {selectedCategory !== 'All' && (
              <span className="flex items-center gap-1.5 px-3 py-1 bg-violet-100 text-violet-700 text-xs font-semibold rounded-full">
                <i className="ri-grid-line text-xs"></i>
                {selectedCategory}
                <button onClick={() => handleCategoryChange('All')} className="ml-0.5 hover:text-violet-900 cursor-pointer"><i className="ri-close-line text-xs"></i></button>
              </span>
            )}
            {selectedTags.map(tag => (
              <span key={tag} className="flex items-center gap-1.5 px-3 py-1 bg-violet-100 text-violet-700 text-xs font-semibold rounded-full">
                <i className="ri-price-tag-3-line text-xs"></i>
                {tag}
                <button onClick={() => handleToggleTag(tag)} className="ml-0.5 hover:text-violet-900 cursor-pointer"><i className="ri-close-line text-xs"></i></button>
              </span>
            ))}
            {showFavoritesOnly && (
              <span className="flex items-center gap-1.5 px-3 py-1 bg-rose-100 text-rose-700 text-xs font-semibold rounded-full">
                <i className="ri-heart-fill text-xs"></i>
                Saved Only
                <button onClick={() => setShowFavoritesOnly(false)} className="ml-0.5 hover:text-rose-900 cursor-pointer"><i className="ri-close-line text-xs"></i></button>
              </span>
            )}
            <button
              onClick={() => { handleCategoryChange('All'); setSelectedTags([]); setShowFavoritesOnly(false); }}
              className="text-xs text-slate-400 hover:text-slate-600 cursor-pointer whitespace-nowrap"
            >
              Clear all
            </button>
          </div>
        )}

        {/* Recently Viewed */}
        <RecentlyViewedRow
          templates={recentlyViewed}
          onPreview={handlePreviewTemplate}
          compareIds={compareIds}
          onToggleCompare={handleToggleCompare}
          favoriteIds={favoriteIds}
          onToggleFavorite={handleToggleFavorite}
        />

        {/* Results header with Sort */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <p className="text-sm text-slate-500">
            Showing <strong className="text-slate-800">{startIndex + 1}–{Math.min(startIndex + ITEMS_PER_PAGE, filteredTemplates.length)}</strong> of <strong className="text-slate-800">{filteredTemplates.length}</strong> templates
            {selectedCategory !== 'All' && <span className="ml-1">in <span className="text-violet-600 font-semibold">{selectedCategory}</span></span>}
            {showFavoritesOnly && <span className="ml-1 text-rose-600 font-semibold">(Saved)</span>}
          </p>
          <div className="flex items-center gap-3">
            {searchQuery && (
              <button onClick={() => handleSearchChange('')} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 cursor-pointer">
                <i className="ri-close-circle-line"></i> Clear search
              </button>
            )}
            {/* Sort Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowSortDropdown(v => !v)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 hover:border-violet-300 hover:text-violet-700 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-sort-line text-base"></i>
                {activeSortLabel}
                <i className={`ri-arrow-down-s-line text-base transition-transform ${showSortDropdown ? 'rotate-180' : ''}`}></i>
              </button>
              {showSortDropdown && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-2xl border border-slate-200 shadow-xl z-30 overflow-hidden">
                  {SORT_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => handleSortChange(opt.value)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors cursor-pointer text-left ${sortBy === opt.value ? 'bg-violet-50 text-violet-700' : 'text-slate-700 hover:bg-slate-50'}`}
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${opt.icon} text-base ${sortBy === opt.value ? 'text-violet-600' : 'text-slate-400'}`}></i>
                      </div>
                      {opt.label}
                      {sortBy === opt.value && <i className="ri-check-line ml-auto text-violet-600 text-sm"></i>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Grid or Empty State */}
        {currentTemplates.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentTemplates.map(template => (
              <TemplateCard
                key={template.id}
                template={template}
                onClick={() => handlePreviewTemplate(template)}
                compareIds={compareIds}
                onToggleCompare={handleToggleCompare}
                isFavorite={favoriteIds.includes(template.id)}
                onToggleFavorite={handleToggleFavorite}
              />
            ))}
          </div>
        ) : showFavoritesOnly && favoriteIds.length === 0 ? (
          <div className="text-center py-24">
            <div className="w-20 h-20 bg-rose-50 rounded-3xl flex items-center justify-center mx-auto mb-5">
              <i className="ri-heart-line text-rose-400 text-4xl"></i>
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">No saved templates yet</h3>
            <p className="text-slate-500 text-sm mb-6 max-w-md mx-auto">Start exploring templates and click the heart icon to save your favorites for quick access later.</p>
            <button 
              onClick={() => setShowFavoritesOnly(false)} 
              className="px-6 py-2.5 bg-rose-600 text-white text-sm font-semibold rounded-xl hover:bg-rose-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Browse All Templates
            </button>
          </div>
        ) : (
          <div className="text-center py-24">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-search-line text-slate-400 text-2xl"></i>
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-2">No templates found</h3>
            <p className="text-slate-500 text-sm mb-4">Try adjusting your search or selecting a different category.</p>
            <button onClick={() => { handleSearchChange(''); handleCategoryChange('All'); setSelectedTags([]); setShowFavoritesOnly(false); }} className="px-5 py-2 bg-violet-600 text-white text-sm font-semibold rounded-xl hover:bg-violet-700 transition-colors cursor-pointer whitespace-nowrap">
              Clear Filters
            </button>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-12">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-9 h-9 flex items-center justify-center rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>
            {getPaginationPages().map((page, idx) =>
              page === '...'
                ? <span key={`ellipsis-${idx}`} className="w-9 h-9 flex items-center justify-center text-slate-400 text-sm">…</span>
                : <button
                    key={page}
                    onClick={() => setCurrentPage(page as number)}
                    className={`w-9 h-9 flex items-center justify-center rounded-xl text-sm font-semibold transition-all cursor-pointer ${
                      currentPage === page
                        ? 'bg-violet-600 text-white shadow-md shadow-violet-200'
                        : 'border border-slate-200 text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {page}
                  </button>
            )}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-9 h-9 flex items-center justify-center rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        )}
      </div>

      {/* CTA Banner */}
      <div className="bg-gradient-to-r from-violet-600 to-fuchsia-600 mt-16">
        <div className="max-w-4xl mx-auto px-6 py-14 text-center text-white">
          <h2 className="text-3xl font-extrabold mb-3">Ready to write your business plan?</h2>
          <p className="text-white/80 mb-8 text-base">Join 500,000+ entrepreneurs who used openclaw.ai to secure funding and launch their businesses.</p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <button
              onClick={() => setShowGeneratePlan(true)}
              className="px-8 py-3.5 bg-white text-violet-700 font-bold rounded-xl hover:bg-slate-50 transition-colors text-sm whitespace-nowrap cursor-pointer shadow-xl flex items-center gap-2"
            >
              <i className="ri-magic-line"></i>
              Generate Custom Plan
            </button>
            <button
              onClick={() => setShowLogin(true)}
              className="px-8 py-3.5 bg-white/15 border border-white/30 text-white font-bold rounded-xl hover:bg-white/25 transition-colors text-sm whitespace-nowrap cursor-pointer"
            >
              Start for Free
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <button onClick={() => navigate('/')} className="cursor-pointer mb-3 block">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-violet-600 to-fuchsia-600 rounded-lg flex items-center justify-center">
                    <i className="ri-cpu-line text-white text-sm"></i>
                  </div>
                  <span className="text-white font-bold text-base">openclaw.ai</span>
                </div>
              </button>
              <p className="text-sm leading-relaxed">The world's most powerful AI platform for business content creation.</p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="/" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="/" className="hover:text-white transition-colors">Templates</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/" className="hover:text-white transition-colors">About</a></li>
                <li><a href="/" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="/" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="/" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-slate-800 flex items-center justify-between flex-wrap gap-4">
            <p className="text-sm">© 2025 openclaw.ai. All rights reserved.</p>
            <div className="flex items-center gap-4 text-sm">
              <a href="/" className="hover:text-white transition-colors"><i className="ri-twitter-x-line"></i></a>
              <a href="/" className="hover:text-white transition-colors"><i className="ri-linkedin-line"></i></a>
              <a href="/" className="hover:text-white transition-colors"><i className="ri-instagram-line"></i></a>
            </div>
          </div>
        </div>
      </footer>

      {/* Modals */}
      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
      {showUpgradeTrial && (
        <UpgradeTrialModal
          toolName="Business Plan Writer"
          usedCount={trialUsed}
          onSignIn={() => { setShowUpgradeTrial(false); setShowLogin(true); }}
          onClose={() => setShowUpgradeTrial(false)}
        />
      )}
      {showGeneratePlan && (
        <GenerateCustomPlanModal
          onClose={() => setShowGeneratePlan(false)}
          onGenerate={() => { setShowGeneratePlan(false); handleUseAction(); }}
        />
      )}
      {previewTemplate && (
        <TemplatePreviewModal
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
          onUse={() => { setPreviewTemplate(null); handleUseAction(); }}
          isFavorite={favoriteIds.includes(previewTemplate.id)}
          onToggleFavorite={handleToggleFavorite}
        />
      )}
      {showCompare && compareTemplates.length >= 2 && (
        <CompareModal
          templates={compareTemplates}
          onClose={() => setShowCompare(false)}
          onUseTemplate={t => { setShowCompare(false); handlePreviewTemplate(t); }}
        />
      )}

      {/* Compare Floating Bar */}
      {compareIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
          <div className="bg-slate-900 text-white rounded-2xl shadow-2xl px-5 py-3.5 flex items-center gap-4 border border-slate-700">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-layout-column-line text-violet-400 text-base"></i>
              </div>
              <span className="text-sm font-semibold">
                {compareIds.length} template{compareIds.length > 1 ? 's' : ''} selected
              </span>
              <span className="text-xs text-slate-400">
                ({MAX_COMPARE - compareIds.length} more allowed)
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCompareIds([])}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-400 hover:text-white hover:bg-slate-700 transition-all cursor-pointer whitespace-nowrap"
              >
                Clear
              </button>
              <button
                disabled={compareIds.length < 2}
                onClick={() => setShowCompare(true)}
                className="px-4 py-1.5 rounded-lg text-xs font-bold bg-violet-600 hover:bg-violet-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5"
              >
                <i className="ri-layout-column-line text-sm"></i>
                Compare Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
